<script setup lang="ts">

import PlayerName from '@/components/Objects/PlayerName.vue'
import type MapCoordinate from '@/types/MapCoordinate.ts'
import {useGameStore} from '@/stores/gameStore.ts'
import {computed} from "vue";
import router  from '@/router'
import PlanetSizeAnimation from '@/components/Elements/Animations/Planet/PlanetSizeAnimation.vue'
import PlanetProductionProgressSimpleObject
  from '@/components/Sections/Planets/PlanetTabs/ProductionTab/Objects/PlanetProductionProgressSimpleObject.vue'
import GenericIconWithColoredNumber from '@/components/Elements/Images/Icons/GenericIconWithColoredNumber.vue'
import { usePlanetStore } from '@/stores/planetStore.ts'
import { useI18n } from 'vue-i18n'

const { t } = useI18n()

const gameStore = useGameStore()
const planetStore = usePlanetStore()

const props = defineProps<{
  coordinate: MapCoordinate
}>();

const planetNameMap: Record<number, string> = {
  1: 'map.planets.lava',
  2: 'map.planets.desert',
  3: 'map.planets.temperate',
  4: 'map.planets.oceanic',
  5: 'map.planets.gas',
  6: 'map.planets.ice',
  7: 'map.planets.ferion',
  8: 'map.planets.ringworld'
}

const planetTypeName = computed(() => {
  const key = planetNameMap[props.coordinate.typeVariant]
  return key ? t(key) : t('map.planets.unknown')
})

const planetData = computed(() => {
  return planetStore.findPlanetById(props.coordinate.typeId)
})

const openPlanet = () => {
  if(props.coordinate.typePlayerId === gameStore.player?.id) {
    router.push('/planets/' + props.coordinate.typeId)
  }
}

</script>

<template>
  <div :class=" {
    'background border-slate-800 border rounded' : true,
    'cursor-pointer' : coordinate.typePlayerId === gameStore.player?.id
  }" @click="openPlanet">
    <div class="flex justify-between">
      <div v-if="coordinate.typeVariant !== 8" class="text-center text-subtext p-1">
        {{ t('map.tooltips.size') }}
        <span :class="{
          'text-green-500' : coordinate.typeSize === gameStore.player?.homeWorldPlanetSize,
          'text-red-500' : coordinate.typeSize === gameStore.player?.homeWorldOppositePlanetSize,
        }">{{ coordinate.typeSize }}</span>
      </div>
      <div class="text-center text-subtext p-1" :class="{ 'w-full': coordinate.typeVariant === 8 }">
        <span :class="{
          'text-green-500' : coordinate.typeVariant === gameStore.player?.homeWorldPlanetType,
          'text-red-500' : coordinate.typeVariant === gameStore.player?.homeWorldOppositePlanetType,
        }">{{ planetTypeName }}</span>
      </div>
    </div>
    <div class="flex justify-center items-center">
      <div class="h-36 w-36 flex justify-center items-center">
        <PlanetSizeAnimation :size="coordinate.typeSize" :type="coordinate.typeVariant"></PlanetSizeAnimation>
      </div>
    </div>
    <div class="text-center p-1">
      <PlayerName :playerId="Number(props.coordinate.typePlayerId)"></PlayerName>
      <span class="ps-2 cursor-help" v-if="!coordinate.currentlyVisible" :title="t('map.tooltips.lastKnownOwner')">H</span>
    </div>
    <div v-if="props.coordinate.typePlayerId === gameStore.player?.id" class="flex justify-between p-1 bg-slate-800">
      <div class="bg-slate-900 rounded p-1 flex-grow w-16">
        <GenericIconWithColoredNumber :amount="Number(planetData?.attackAgainstShips)" type="attack_against_ships" :tooltip="t('map.tooltips.activeDefenses')"/>
      </div>
      <div class="bg-slate-900 rounded p-1  flex-grow mx-1 w-16">
        <GenericIconWithColoredNumber :amount="Number(planetData?.shields)" type="shield" :tooltip="t('map.tooltips.shieldStrength')"/>
      </div>
      <div class="bg-slate-900 rounded flex-grow p-1 w-16">
        <GenericIconWithColoredNumber :amount="Number(planetData?.energy)" type="energy_storage" :tooltip="t('map.tooltips.shieldEnergy')"/>
      </div>
    </div>
    <div v-if="planetData !== undefined && planetData!.productionObjectId>0" class="px-1 pb-1 bg-slate-800">
      <div class="bg-slate-900 rounded p-2 pt-1 flex-grow">
      <PlanetProductionProgressSimpleObject :planetId="props.coordinate.typeId" :showPQA="false"/>
      </div>
    </div>

  </div>
</template>

<style scoped lang="scss">
.background {
  background-image: url("https://cdn.ferion.com/images/backgrounds/space.webp");
}

.planet-image-3 {
  height: 10rem;
  width: 10rem;
}
.planet-image-2 {
  height: 5rem;
  width: 5rem;
  margin: 2.5rem;
}
.planet-image-1 {
  height: 2rem;
  width: 2rem;
  margin: 4rem;
}
</style>